import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import FormPageLayout from "../../components/common/formkit/FormPageLayout";
import { Section, TextField, SegmentField } from "../../components/common/formkit/fields";
import { FocusFirstError, useUnsavedChangesGuard, allowLeaveOnce, useExitWizard } from "../../components/common/formkit/formHelpers";
import { showToast } from "../../components/common/notification/Notification";
import { getErrorText } from "../partnerNetwork/services/partnerNetworkService";
import { useTeamService } from "./hooks/useTeamRecords";
import { DEFAULT_MEMBER_ROLE, MEMBER_ROLES, SUBSCRIPTION_PATH, TEAMS_HOME } from "./constants/teamEndpoints";
import "../partnerNetwork/partnerNetwork.css";

const FORM_ID = "team-member-form";
const ROLE_OPTIONS = MEMBER_ROLES.map((role) => ({ value: role, label: role }));

// The backend creates a login for the member from the mobile number, so it is required (10 digits, as in the
// other partner forms). A name is the minimum the member list needs to show somebody.
const validationSchema = Yup.object({
  first_name: Yup.string().trim().required("First name is required"),
  last_name: Yup.string().trim(),
  phone: Yup.string().required("Mobile number is required").matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
  email: Yup.string().trim().email("Enter a valid email address"),
  role: Yup.string().oneOf(MEMBER_ROLES).required("Role is required"),
});

const initialValues = { first_name: "", last_name: "", phone: "", email: "", role: DEFAULT_MEMBER_ROLE };

/** Lives inside <Formik> so it can read the form state for the unsaved-changes warning. */
const MemberFormBody = ({ saved, submitError }) => {
  const { dirty, setFieldValue } = useFormikContext();
  useUnsavedChangesGuard(dirty && !saved);

  return (
    <Form id={FORM_ID} noValidate>
      <FocusFirstError />

      {submitError && (
        <div className="alert alert-danger pn-alert" role="alert">
          {submitError}
        </div>
      )}

      <Section title="Member details" hint="Add somebody to this team and choose what they can do.">
        <div className="fk-grid">
          <TextField name="first_name" label="First name" required placeholder="Enter first name" autoComplete="off" />
          <TextField name="last_name" label="Last name" placeholder="Enter last name" autoComplete="off" />
          <TextField
            name="phone"
            label="Mobile number"
            required
            type="tel"
            inputMode="numeric"
            maxLength={10}
            placeholder="Enter 10-digit mobile number"
            autoComplete="off"
            onChange={(e) => setFieldValue("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
          />
          <TextField name="email" label="Email address" type="email" placeholder="Enter email address" autoComplete="off" />
          <SegmentField name="role" label="Role" required options={ROLE_OPTIONS} className="fk-col-full" />
        </div>
      </Section>
    </Form>
  );
};

/** /partner/team/:Id/members/new - the Add Member flow of the mobile Members screen, as a full page */
const TeamMemberFormPage = () => {
  const { Id: teamId } = useParams();
  const teamPath = `${TEAMS_HOME}/${teamId}`;
  const exit = useExitWizard(teamPath);
  const service = useTeamService();

  const [seats, setSeats] = useState(null);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    service.fetchSeats().then((count) => {
      if (!cancelled) setSeats(count);
    });
    return () => {
      cancelled = true;
    };
  }, [service]);

  const handleSubmit = async (values, { setFieldError }) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await service.addMember(teamId, {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        phone: values.phone,
        email: values.email.trim(),
        role: values.role,
      });
      showToast("Member added successfully", { type: "success" });
      setSaved(true); // lifts the unsaved-changes warning before leaving
      allowLeaveOnce();
      setTimeout(exit, 0);
    } catch (err) {
      // The form keeps its values so nothing is lost
      const text = getErrorText(err, "Unable to add member");
      if (/mobile number/i.test(text)) setFieldError("phone", text);
      setSubmitError(text);
      showToast(text, { type: "error" });
      setSubmitting(false);
    }
  };

  // Mobile swaps the add button for "Pay Now" while the plan has no seats
  if (seats === 0) {
    return (
      <div className="fk-page" style={{ maxWidth: 860 }}>
        <div className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-3" role="alert">
          <span>Your current plan has no seats left for team members. Choose a plan with seats to add members.</span>
          <div className="d-flex gap-2">
            <Link to={SUBSCRIPTION_PATH} className="btn btn-sm btn-primary">
              View plans
            </Link>
            <Link to={teamPath} className="btn btn-sm btn-outline-secondary">
              Back to team
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FormPageLayout
      title="Add Member"
      subtitle="Add a member to this team."
      formId={FORM_ID}
      submitLabel="Add Member"
      submitting={submitting}
      loading={seats === null}
      onCancel={exit}
    >
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        <MemberFormBody saved={saved} submitError={submitError} />
      </Formik>
    </FormPageLayout>
  );
};

export default TeamMemberFormPage;
